import { z } from "zod";
import { LineStringGeometry, PointGeometry } from "./geo";

// Fire-protection layers from the Forest Data Bank (Bank Danych o Lasach, Lasy Państwowe, open
// data), issue #299. One ArcGIS service (WMS_BDL_dojazdy_pozarowe) carries the layers we draw as
// vectors, replacing the raster WMS overlay (#267) that could not be styled — the same move forest
// compartments made in #197. The server fetches them in XYZ tiles and keeps them; phones keep the
// tiles they got, for offline use. A tile carries both kinds (one upstream call per tile).

export const FIRE_TILE_ZOOM = 13;
/** Below this map zoom the features are too small/dense to read: nothing is fetched or drawn. */
export const FIRE_MIN_ZOOM = 11;
export const FIRE_ATTRIBUTION =
  "Dojazdy pożarowe i punkty wody: Bank Danych o Lasach © Lasy Państwowe";

/** Fire-access road (dojazd pożarowy): a line fire engines can use. */
export const FireRoad = z.object({
  type: z.literal("Feature"),
  geometry: LineStringGeometry,
  properties: z.object({
    id: z.string(),
    /** Road width in metres, if the source gives it. */
    width: z.number().nullable(),
  }),
});
export type FireRoad = z.infer<typeof FireRoad>;

/** Water-draw point (punkt czerpania wody): where fire engines can refill. */
export const WaterPoint = z.object({
  type: z.literal("Feature"),
  geometry: PointGeometry,
  properties: z.object({
    id: z.string(),
    /** Free-text location description from the source, e.g. "z drogi gruntowej w Starym Gronowie". */
    name: z.string().nullable(),
  }),
});
export type WaterPoint = z.infer<typeof WaterPoint>;

/** One tile's two fire layers, each a GeoJSON FeatureCollection. */
export const FireTile = z.object({
  roads: z.object({
    type: z.literal("FeatureCollection"),
    features: z.array(FireRoad),
  }),
  water: z.object({
    type: z.literal("FeatureCollection"),
    features: z.array(WaterPoint),
  }),
});
export type FireTile = z.infer<typeof FireTile>;

type RoadFC = FireTile["roads"];
type WaterFC = FireTile["water"];

/** Merge tiles into one FeatureCollection per kind; a feature crossing a tile edge is drawn once. */
export function mergeFireTiles(tiles: Iterable<FireTile>): FireTile {
  const roads = new Map<string, FireRoad>();
  const water = new Map<string, WaterPoint>();
  for (const t of tiles) {
    for (const f of t.roads.features) roads.set(f.properties.id, f);
    for (const f of t.water.features) water.set(f.properties.id, f);
  }
  return {
    roads: { type: "FeatureCollection", features: [...roads.values()] } satisfies RoadFC,
    water: { type: "FeatureCollection", features: [...water.values()] } satisfies WaterFC,
  };
}
